/**
 * Reanalysis batch progress + history (project_management#514).
 *
 * This tab is the operator's answer to "is it running, how far along, and what
 * failed". It reads the same persisted per-item state that any later debugging
 * would query — there is no separate progress store to drift out of sync.
 *
 * Polls while a batch is live rather than pushing over a subscription: a batch
 * ticks at most every 15 s (the pump interval), so a 10 s poll is already
 * finer-grained than the underlying data changes.
 *
 * project_management#550: both grids page on the server (they used fixed page
 * sizes over a truncated first page, so rows-per-page and next/previous did
 * nothing), the history can be searched by patient userId, items show the
 * recording file name, and the patient/batch live in the URL so a filtered
 * view can be shared or reopened.
 *
 * project_management#574: a batch may span several patients — the history
 * shows "N patients", items have a Patient column and can be filtered by
 * patient, and a finished batch's failed items can be retried as a new batch
 * (same reason + " (retry)").
 */
import React from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Link from '@material-ui/core/Link';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';

import { LoadingSpinner } from '../../LoadingSpinner';
import {
  CANCEL_REANALYSIS_BATCH,
  GET_REANALYSIS_BATCH,
  GET_REANALYSIS_BATCHES,
  PAUSE_REANALYSIS_BATCH,
  RESUME_REANALYSIS_BATCH,
  RETRY_FAILED_REANALYSIS_ITEMS,
} from './queries';
import {
  BATCH_PAGE_SIZES,
  BatchItem,
  BatchSummary,
  HistoryUrlState,
  ITEM_PAGE_SIZES,
  LIVE_STATUSES,
  canRetryFailed,
  focusBatchId,
  itemPatientOptions,
  itemRowCount,
  pageVariables,
  patientLabel,
  readHistoryUrlState,
  retryReasonOf,
  toBatchRows,
  toItemRows,
  writeHistoryUrlState,
} from './batchHistory';

const POLL_INTERVAL_MS = 10_000;

const useStyles = makeStyles((theme) => ({
  container: { width: 1400, marginTop: theme.spacing(4) },
  actions: { marginBottom: theme.spacing(2), '& > *': { marginRight: theme.spacing(1) } },
  section: { marginTop: theme.spacing(3) },
  search: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& > *': { marginRight: theme.spacing(1) },
  },
}));

const BATCH_COLUMNS: GridColDef[] = [
  { field: 'id', headerName: 'Batch ID', width: 220 },
  { field: 'scope', headerName: 'Scope', width: 110 },
  { field: 'status', headerName: 'Status', width: 110 },
  // #574: "N patients" for a batch spanning several.
  { field: 'patient', headerName: 'Patient', width: 220 },
  { field: 'reason', headerName: 'Reason', width: 260 },
  { field: 'progress', headerName: 'Progress', width: 140 },
  { field: 'failed', headerName: 'Failed', width: 90 },
  { field: 'createdAt', headerName: 'Created', width: 200 },
];

const itemColumns = (scope: string | undefined): GridColDef[] => [
  {
    field: 'targetId',
    headerName: 'Target ID',
    width: 220,
    // Recordings open in the admin recording view; there is no admin page per sequence.
    renderCell: (params: GridCellParams) =>
      scope === 'recording' ? (
        <Link component={RouterLink} to={`/admin-dashboard/recording?id=${params.value}`}>
          {String(params.value)}
        </Link>
      ) : (
        <span>{String(params.value)}</span>
      ),
  },
  // #574: a batch may span patients; the item's owner.
  { field: 'userId', headerName: 'Patient', width: 220 },
  // #550: the operator's downloaded dataset is keyed by file name, not by id.
  { field: 'fileName', headerName: 'File name', width: 170 },
  { field: 'state', headerName: 'State', width: 110 },
  { field: 'attempts', headerName: 'Attempts', width: 100 },
  { field: 'durationMs', headerName: 'Duration (ms)', width: 130 },
  { field: 'completedAt', headerName: 'Completed', width: 200 },
  // The reason this tab exists: failures keep their error forever.
  { field: 'error', headerName: 'Error', width: 420 },
];

export function ReanalysisManagement() {
  const classes = useStyles();
  const [params, setParams] = useSearchParams();
  const urlState = readHistoryUrlState(params);
  const { patient } = urlState;

  const [patientInput, setPatientInput] = React.useState(patient);
  const [batchPage, setBatchPage] = React.useState(0);
  const [batchPageSize, setBatchPageSize] = React.useState(BATCH_PAGE_SIZES[0]);
  const [failuresOnly, setFailuresOnly] = React.useState(false);
  const [itemPageSize, setItemPageSize] = React.useState(ITEM_PAGE_SIZES[0]);
  // The item page belongs to one (batch, filter) view: switching either starts
  // at page 0 without an effect-driven double fetch.
  const [itemPaging, setItemPaging] = React.useState({ key: '', page: 0 });
  // The item patient filter belongs to one batch: picking another resets it.
  const [itemPatientPick, setItemPatientPick] = React.useState({ batchId: '', userId: '' });
  // "Retry failed" asks once before queueing a new batch (#574).
  const [retryAsked, setRetryAsked] = React.useState<string | null>(null);

  const updateUrl = (next: Partial<HistoryUrlState>) =>
    setParams(writeHistoryUrlState(params, { ...urlState, ...next }), { replace: true });

  const applyPatient = (value: string) => {
    setBatchPage(0);
    updateUrl({ patient: value.trim(), batchId: null });
  };

  const batchesQuery = useQuery(GET_REANALYSIS_BATCHES, {
    variables: {
      ...pageVariables(batchPage, batchPageSize),
      ...(patient ? { targetUserId: patient } : {}),
    },
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'cache-and-network',
  });

  // previousData keeps the grid on screen while the next page loads.
  const history = (batchesQuery.data ?? batchesQuery.previousData)?.getReanalysisBatches;
  const batches: BatchSummary[] = React.useMemo(() => history?.batches ?? [], [history]);
  const totalCount: number = history?.totalCount ?? 0;

  const focusedId = focusBatchId({ selectedBatchId: urlState.batchId, batches, patient });
  const itemPatient = itemPatientPick.batchId === focusedId ? itemPatientPick.userId : '';
  const itemKey = `${focusedId}:${failuresOnly}:${itemPatient}`;
  const itemPage = itemPaging.key === itemKey ? itemPaging.page : 0;

  const detailQuery = useQuery(GET_REANALYSIS_BATCH, {
    variables: {
      batchId: focusedId,
      itemLimit: itemPageSize,
      itemOffset: itemPage * itemPageSize,
      ...(failuresOnly ? { itemState: 'failed' } : {}),
      ...(itemPatient ? { itemUserId: itemPatient } : {}),
    },
    skip: !focusedId,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'cache-and-network',
  });

  const [pauseBatch] = useMutation(PAUSE_REANALYSIS_BATCH);
  const [resumeBatch] = useMutation(RESUME_REANALYSIS_BATCH);
  const [cancelBatch] = useMutation(CANCEL_REANALYSIS_BATCH);
  const [retryFailed, retryState] = useMutation(RETRY_FAILED_REANALYSIS_ITEMS);
  const retryResult = retryState.data?.retryFailedReanalysisItems;

  const detail = (detailQuery.data ?? detailQuery.previousData)?.getReanalysisBatch;
  const focused = focusedId ? detail?.batch : undefined;
  const counts = focused?.counts;
  const settled = counts ? counts.succeeded + counts.failed + counts.skipped : 0;
  const percent = counts?.total ? Math.round((settled / counts.total) * 100) : 0;

  const batchRows = React.useMemo(() => toBatchRows(batches), [batches]);
  const items: BatchItem[] = React.useMemo(() => focused?.items ?? [], [focused]);
  const itemRows = React.useMemo(() => toItemRows(items), [items]);
  const columns = React.useMemo(() => itemColumns(focused?.scope), [focused?.scope]);
  const patientOptions = itemPatientOptions(focused);

  const handleRetry = async (batchId: string) => {
    setRetryAsked(null);

    // A transport error is shown from `retryState.error`; don't leak a rejection.
    const result = await retryFailed({ variables: { batchId } }).catch(() => null);
    const next = result?.data?.retryFailedReanalysisItems;

    if (next?.success && next.batch?.id) {
      setFailuresOnly(false);
      updateUrl({ batchId: next.batch.id });
      batchesQuery.refetch();
    }
  };

  if (batchesQuery.loading && !history) return <LoadingSpinner />;

  if (batchesQuery.error) {
    return <div>Error loading reanalysis batches: {batchesQuery.error.message}</div>;
  }

  return (
    <Container className={classes.container} maxWidth={false}>
      {!patient && !totalCount && (
        <Alert severity="info">
          <AlertTitle>No reanalysis batches yet</AlertTitle>
          Select recordings (of one or several patients) or one patient’s sequences on their tab,
          then use “Reanalyze selected”.
        </Alert>
      )}

      {focusedId && detail && !detail.success && (
        <Alert severity="warning">
          {detail.error ?? `Reanalysis batch ${focusedId} could not be loaded.`}
        </Alert>
      )}

      {focused && (
        <div className={classes.section}>
          <Typography variant="h6">
            {focused.scope} batch — {focused.status}
          </Typography>
          <Typography color="textSecondary" variant="body2">
            {focused.reason} ·{' '}
            {focused.targetUserId ? `patient ${focused.targetUserId}` : patientLabel(focused)} ·{' '}
            {focused.concurrency} at a time
            {focused.retryOf ? ` · retry of ${focused.retryOf}` : ''}
          </Typography>

          <LinearProgress
            style={{ marginTop: 12, marginBottom: 8 }}
            value={percent}
            variant="determinate"
          />
          <Typography variant="body2">
            {settled}/{counts?.total} settled · {counts?.succeeded} ok · {counts?.failed} failed ·{' '}
            {counts?.skipped} skipped · {counts?.inFlight} in flight · {counts?.pending} pending
          </Typography>

          <div className={classes.actions} style={{ marginTop: 12 }}>
            {focused.status === 'running' && (
              <Button
                variant="outlined"
                onClick={() => pauseBatch({ variables: { batchId: focused.id } })}
              >
                Pause
              </Button>
            )}
            {focused.status === 'paused' && (
              <Button
                variant="outlined"
                onClick={() => resumeBatch({ variables: { batchId: focused.id } })}
              >
                Resume
              </Button>
            )}
            {LIVE_STATUSES.includes(focused.status) && (
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => cancelBatch({ variables: { batchId: focused.id } })}
              >
                Cancel
              </Button>
            )}
            <Button variant="outlined" onClick={() => setFailuresOnly((value) => !value)}>
              {failuresOnly ? 'Show all items' : `Show failures (${counts?.failed ?? 0})`}
            </Button>
            {canRetryFailed(focused) && (
              <Button
                color="primary"
                disabled={retryState.loading}
                variant="outlined"
                onClick={() => setRetryAsked(focused.id)}
              >
                {retryState.loading ? 'Queueing retry…' : `Retry failed (${counts?.failed ?? 0})`}
              </Button>
            )}
            {patientOptions.length > 0 && (
              <TextField
                inputProps={{ 'aria-label': 'Filter items by patient' }}
                label="Patient"
                size="small"
                style={{ minWidth: 260 }}
                value={itemPatient}
                variant="outlined"
                select
                onChange={(event) =>
                  setItemPatientPick({ batchId: focused.id, userId: String(event.target.value) })}
              >
                <MenuItem value="">All patients ({patientOptions.length})</MenuItem>
                {patientOptions.map((userId) => (
                  <MenuItem key={userId} value={userId}>
                    {userId}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </div>

          {retryAsked === focused.id && (
            <Alert
              action={(
                <>
                  <Button color="inherit" size="small" onClick={() => handleRetry(focused.id)}>
                    Queue retry
                  </Button>
                  <Button color="inherit" size="small" onClick={() => setRetryAsked(null)}>
                    Cancel
                  </Button>
                </>
              )}
              severity="info"
              style={{ marginBottom: 12 }}
            >
              Queue a new batch of the {counts?.failed} failed items, reason “
              {retryReasonOf(focused.reason)}”?
            </Alert>
          )}
          {retryResult && !retryResult.success && (
            <Alert severity="error" style={{ marginBottom: 12 }}>
              <AlertTitle>Retry not queued</AlertTitle>
              {retryResult.error}
            </Alert>
          )}
          {retryState.error && (
            <Alert severity="error" style={{ marginBottom: 12 }}>
              <AlertTitle>Retry not queued</AlertTitle>
              {retryState.error.message}
            </Alert>
          )}

          <div style={{ width: '100%' }}>
            <DataGrid
              columns={columns}
              loading={detailQuery.loading}
              page={itemPage}
              pageSize={itemPageSize}
              paginationMode="server"
              rowCount={itemRowCount(focused, failuresOnly)}
              rows={itemRows}
              rowsPerPageOptions={ITEM_PAGE_SIZES}
              autoHeight
              disableSelectionOnClick
              pagination
              onPageChange={(page) => setItemPaging({ key: itemKey, page })}
              onPageSizeChange={(size) => {
                setItemPageSize(size);
                setItemPaging({ key: itemKey, page: 0 });
              }}
            />
          </div>
        </div>
      )}

      <div className={classes.section}>
        <Typography variant="h6">Batch history</Typography>
        <form
          className={classes.search}
          onSubmit={(event) => {
            event.preventDefault();
            applyPatient(patientInput);
          }}
        >
          <TextField
            inputProps={{ 'aria-label': 'Patient userId' }}
            label="Patient userId"
            size="small"
            style={{ width: 320 }}
            value={patientInput}
            variant="outlined"
            onChange={(event) => setPatientInput(event.target.value)}
          />
          <Button color="primary" type="submit" variant="contained">
            Search
          </Button>
          {patient && (
            <Button
              variant="outlined"
              onClick={() => {
                setPatientInput('');
                applyPatient('');
              }}
            >
              Clear
            </Button>
          )}
          {patient && (
            <Typography color="textSecondary" variant="body2">
              {totalCount} batch{totalCount === 1 ? '' : 'es'} for patient {patient}
            </Typography>
          )}
        </form>
        {patient && !totalCount && !batchesQuery.loading && (
          <Alert severity="info">No reanalysis batches for patient {patient}.</Alert>
        )}
        <div style={{ width: '100%' }}>
          <DataGrid
            columns={BATCH_COLUMNS}
            loading={batchesQuery.loading}
            page={batchPage}
            pageSize={batchPageSize}
            paginationMode="server"
            rowCount={totalCount}
            rows={batchRows}
            rowsPerPageOptions={BATCH_PAGE_SIZES}
            autoHeight
            disableSelectionOnClick
            pagination
            onPageChange={setBatchPage}
            onPageSizeChange={(size) => {
              setBatchPageSize(size);
              setBatchPage(0);
            }}
            onRowClick={(row) => updateUrl({ batchId: String(row.id) })}
          />
        </div>
      </div>
    </Container>
  );
}
